import { useEffect, useState } from "react";
import selectors from "selectors";
import { fetchPatientSignatures } from "actions/signatures";
import { mintPapyrusOtt } from "actions/handoff";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "components/ui_new";
import BreadCrumbs from "components/ui_new/breadcrumbs";
import { Page } from "components/ui";
import { formatDate } from "utils/misc";
import CalandarIcon from "assets/images/calendar.png";
import { DOCUSIGN_HOST } from "environment";
import NotificationManager from "utils/notifications";
import "./signature.scss";
import useTranslate from "hooks/use-translate-language";
import useDocumentTitle from "hooks/use-document-title";

const Signatures = () => {
  const translate = useTranslate();
  const dispatch = useDispatch();
  const patientSignatures = useSelector(selectors.getPatientSignatures);
  const [isFetching, setIsFetching] = useState(false);
  // signature_assignment_id currently minting an OTT, or null when idle.
  const [mintingFor, setMintingFor] = useState(null);

  useDocumentTitle(translate("mySignatures"));

  useEffect(() => {
    setIsFetching(true);
    dispatch(fetchPatientSignatures()).finally(() => {
      setIsFetching(false);
    });
  }, [dispatch]);

  return (
    <Page.Container>
      <Page.Header title={translate("mySignatures")} goBack />
      <Page.Content
        isFetching={isFetching}
        isEmpty={patientSignatures.length === 0}
        emptyMessage={translate("noDocuments")}
        styles={{ flex: "none" }}
      >
        <div className="signatures-page-content">
          <div className="signatures-page-header">
            <BreadCrumbs
              parentLinkLabel="home"
              childLabel="signature"
              title="signature"
              titleClassname="signatures-title"
            />
          </div>
        {patientSignatures.map(sig => (
          <div key={sig.signature_assignment_id} className="signature-item">
            <div>
              <h2 className="signature-title">{sig.title}</h2>
              <div className="date-sent-container">
                <img src={CalandarIcon} alt="" />
                <span className="signature-date-sent">
                  {formatDate(sig.date_sent)}
                </span>
              </div>
              {sig.status === 0 ? (
                <div className="signature-status pending">
                  {translate("unsigned")}
                </div>
              ) : (
                <div className="signature-status signed">
                  {`${translate("signed")}: ${formatDate(sig.date_signed)}`}
                </div>
              )}
            </div>
            <div className="signature-action-btn">
              {sig.status === 0 ? (
                <Button
                  variant="contained"
                  color="primary"
                  aria-label={`${translate("signNow")} ${sig.title}`}
                  disabled={mintingFor === sig.signature_assignment_id}
                  onClick={async () => {
                    // Guard against double-clicks: each one would mint another
                    // OTT and open another window.
                    if (mintingFor) return;

                    const { innerWidth: width, innerHeight: height } = window;
                    // Open synchronously (empty) to keep the click's popup
                    // activation; set its location only after the OTT is minted
                    // (a window.open after the await would be popup-blocked).
                    // Uses a one-time exchange token instead of the reusable
                    // session token in the URL (S3DEV2-7560).
                    const new_window = window.open(
                      "",
                      "_blank",
                      "height=" + height + ", width=" + width
                    );

                    if (!new_window) {
                      // Popup blocker. Say so — silently doing nothing leaves
                      // the patient with no idea why Sign Now did nothing.
                      NotificationManager.error(
                        "popupBlocked",
                        "popupBlockedMessage"
                      );
                      return;
                    }

                    setMintingFor(sig.signature_assignment_id);
                    let ott;
                    try {
                      ott = await dispatch(mintPapyrusOtt());
                    } catch (error) {
                      console.error(error.message);
                    } finally {
                      setMintingFor(null);
                    }

                    if (!ott) {
                      new_window.close();
                      // A 401 already notifies and redirects from the api
                      // middleware; everything else would otherwise be silent.
                      NotificationManager.error(
                        "signatureUnavailable",
                        "signatureUnavailableMessage"
                      );
                      return;
                    }

                    new_window.location = `${DOCUSIGN_HOST}/signature?ott=${encodeURIComponent(
                      ott
                    )}&template_id=${sig.html_document_template_id}&signature_id=${sig.signature_assignment_id}`;
                    const timer = setInterval(() => {
                      if (new_window.closed) {
                        clearInterval(timer);
                        dispatch(fetchPatientSignatures());
                      }
                    }, 1000);
                  }}
                >
                  {translate("signNow")}
                </Button>
              ) : (
                <Button
                  variant="outlined"
                  color="primary"
                  aria-label={`${translate("view")} ${sig.title}`}
                  onClick={() => {
                    window.open(sig.download_url);
                  }}
                >
                  {translate("view")}
                </Button>
              )}
            </div>
          </div>
        ))}
        </div>
      </Page.Content>
    </Page.Container>
  );
};

export default Signatures;
